import { useEffect } from 'react';

import {
  getRumFeatureKeyFromWindow,
  getRumPageKeyFromWindow,
  recordRumCtaClick,
  sanitizeIdentifier,
  syncRumSessionIdentityFromStorage
} from './rumAnalytics';

const MAJOR_ACTION_KEYWORDS = [
  'add',
  'apply',
  'assign',
  'confirm',
  'copy',
  'create',
  'delete',
  'download',
  'edit',
  'export',
  'grant',
  'import',
  'login',
  'logout',
  'move',
  'next',
  'reboot',
  'renew',
  'restore',
  'run',
  'save',
  'submit',
  'troubleshoot',
  'update',
  'upload'
];

function isDisabled(element) {
  return (
    element?.disabled ||
    element?.getAttribute('aria-disabled') === 'true' ||
    element?.closest?.('[aria-disabled="true"]')
  );
}

function getComponentType(element) {
  if (!element) return 'button';
  const role = element.getAttribute('role');
  const className = String(element.className || '');

  if (role === 'tab' || className.includes('MuiTab-root')) {
    return 'tab';
  }

  if (element.tagName === 'A') {
    return 'link';
  }

  return 'button';
}

function findActionKeyword(element) {
  const candidates = [
    element?.getAttribute?.('data-rum-action-id'),
    element?.getAttribute?.('data-field-label'),
    element?.getAttribute?.('aria-label'),
    element?.textContent
  ];

  for (const candidate of candidates) {
    const normalized = sanitizeIdentifier(candidate);
    if (!normalized) continue;

    for (const keyword of MAJOR_ACTION_KEYWORDS) {
      if (normalized === keyword || normalized.startsWith(`${keyword}_`) || normalized.endsWith(`_${keyword}`) || normalized.includes(`_${keyword}_`)) {
        return keyword;
      }
    }
  }

  return '';
}

function getStableActionId(element, componentType) {
  const explicitId =
    element?.getAttribute?.('data-rum-action-id') ||
    element?.id ||
    element?.getAttribute?.('data-testid') ||
    element?.getAttribute?.('data-id') ||
    element?.getAttribute?.('aria-controls') ||
    element?.getAttribute?.('name');

  if (explicitId) {
    return sanitizeIdentifier(explicitId);
  }

  if (componentType === 'tab') {
    return (
      sanitizeIdentifier(element?.getAttribute?.('aria-label')) ||
      sanitizeIdentifier(element?.textContent) ||
      'tab'
    );
  }

  return findActionKeyword(element);
}

function isMajorCta(element, componentType, actionId) {
  if (!element) return false;
  if (componentType === 'tab') return Boolean(actionId);
  if (!actionId) return false;
  if (element.dataset.rumTrack === 'true') return true;

  const parentSurface = element.closest('[data-rum-surface]')?.dataset?.rumSurface || '';
  const className = String(element.className || '');
  const isMuiActionButton =
    className.includes('MuiButton-contained') || className.includes('MuiButton-outlined');
  const hasMajorKeyword = MAJOR_ACTION_KEYWORDS.some(
    (keyword) =>
      actionId === keyword ||
      actionId.startsWith(`${keyword}_`) ||
      actionId.endsWith(`_${keyword}`) ||
      actionId.includes(`_${keyword}_`)
  );

  if (parentSurface === 'drawer-footer') return true;
  if (hasMajorKeyword && isMuiActionButton) return true;
  if (hasMajorKeyword && (element.id || element.getAttribute('data-testid') || element.getAttribute('data-field-label'))) {
    return true;
  }

  return false;
}

export default function RumClickTracker() {
  useEffect(() => {
    syncRumSessionIdentityFromStorage();

    const handleDocumentClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const actionElement = target.closest('button,[role="button"],[role="tab"],a,.MuiTab-root,.MuiButton-root');
      if (!actionElement) return;
      if (actionElement.closest('[data-rum-nav-item="true"]')) return;
      if (isDisabled(actionElement)) return;

      const componentType = getComponentType(actionElement);
      const actionId = getStableActionId(actionElement, componentType);
      if (!isMajorCta(actionElement, componentType, actionId)) return;

      recordRumCtaClick({
        featureKey: actionElement.getAttribute('data-rum-feature-key') || getRumFeatureKeyFromWindow(),
        pageKey: actionElement.getAttribute('data-rum-page-key') || getRumPageKeyFromWindow(),
        actionId,
        componentType
      });
    };

    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, []);

  return null;
}
